import { BUILD_ENVIRONMENT_NAMES } from '@/env-config';

import { buildProductUrl } from './utils/product-url-builder';

export enum ConnectorIds {
  FormstackForm = 'forms',
  F4SF = 'f4sf',
  Sign = 'sign',
  Documents = 'documents',
  StepLogic = 'step_logic',
  Approval = 'approval',
  API = 'api',
}

export enum Products {
  Copilot = 'dobby',
  FormstackClassic = 'formstack_classic',
  Documents = 'webmerge',
  FSID = 'fsid',
  Platform = 'platform',
  Sync = 'sync',
  Sign = 'insure_sign',
  SalesforceApp = 'salesforce',
}

export enum Addons {
  CopilotPro = 'copilot-pro',
}

export enum AdditionalFeatures {
  ConditionalLogic = 'conditionalLogic',
  Approvals = 'approvals',
  ApiConnector = 'apiConnector',
  Workspace = 'workspace',
}

export enum WorkflowListSortKeys {
  Name = 'name',
  CreatedDate = 'createdDate',
}

export enum TaskListSortKeys {
  Name = 'name',
  Status = 'status',
}

export enum AppListSortKeys {
  Name = 'name',
  DateAdded = 'dateAdded',
}

export enum GonePageScope {
  Workspace = 'workspace',
}

export const SIGN_URL = 'https://my.insuresign.com/';

const { STAGING, QA, TESTING, PRODUCTION, UNIT_TESTING } = BUILD_ENVIRONMENT_NAMES;
export const FORMS_DOMAIN_MAP = {
  [TESTING]: 'https://www.testing-formstack.com',
  [QA]: 'https://www.qa-formstack.com',
  [STAGING]: 'https://www.staging-formstack.com',
  [PRODUCTION]: 'https://www.formstack.com',
  [UNIT_TESTING]: 'https://www.unit-testing-formstack.com',
};

export const DOCS_DOMAIN_MAP = {
  [TESTING]: 'https://testing-webmerge.me',
  [QA]: 'https://qa-webmerge.me',
  [STAGING]: 'https://staging-webmerge.me',
  [PRODUCTION]: 'https://www.webmerge.me',
  [UNIT_TESTING]: 'https://unit-testing-webmerge.me',
};

export const SIGN_DOMAIN_MAP = {
  [TESTING]: 'https://dev.insuresign.com',
  [QA]: 'https://dev.insuresign.com',
  [STAGING]: 'https://dev.insuresign.com',
  [PRODUCTION]: 'https://my.insuresign.com',
  [UNIT_TESTING]: 'https://unit-testing-sign.com',
};

export const FSID_DOMAIN_MAP = {
  [TESTING]: 'https://admin.testing-formstack.com',
  [QA]: 'https://admin.qa-formstack.com',
  [STAGING]: 'https://admin.staging-formstack.com',
  [PRODUCTION]: 'https://admin.formstack.com',
  [UNIT_TESTING]: 'https://admin.unit-testing-formstack.com',
};

export const connectorHasProduct = (
  connectorId: ConnectorIds
): connectorId is keyof typeof PRODUCT_CONNECTOR_MAP => connectorId in PRODUCT_CONNECTOR_MAP;

export const PRODUCT_CONNECTOR_MAP = {
  [ConnectorIds.FormstackForm]: 'formstack_classic',
  [ConnectorIds.Documents]: 'webmerge',
  [ConnectorIds.Sign]: 'insure_sign',
};

export const FORMSTACK_FORMS_NEW_FORM_URL = buildProductUrl(
  Products.FormstackClassic,
  'admin/platform-sso/start?redirect=/admin/account/dashboard?createNewForm=1'
);
export const FORMSTACK_DOCS_NEW_DOC_URL = buildProductUrl(
  Products.Documents,
  'manage/documents?newdoc'
);
export const FORMSTACK_DOCS_MANAGE_DOCUMENTS_URL = buildProductUrl(
  Products.Documents,
  'manage/documents'
);
export const FORMSTACK_SIGN_NEW_SIGNATURE_URL = buildProductUrl(Products.Sign, '#/fsid-login');

export const FSID_BILLING_URL = buildProductUrl(Products.FSID, 'billing/manage');

export enum ConnectorFilterKeys {
  All = 'all',
}

export const BASE_URL = '/';

export const ADMIN_PATH = '/admin';
export const HOME_PATH = `${ADMIN_PATH}/home`;
export const TEMPLATE_PATH = `${ADMIN_PATH}/template`;
export const WORKFLOWS_PATH = `${ADMIN_PATH}/workflows`;
export const WORKSPACE_PATH = `${ADMIN_PATH}/workspace`;
export const SETTINGS_PATH = `${ADMIN_PATH}/settings`;
export const APP_CONNECTIONS_PATH = `${SETTINGS_PATH}/apps`;

export const APP_PATHS = {
  [HOME_PATH]: {
    path: HOME_PATH,
    end: false,
  },
  [WORKFLOWS_PATH]: {
    path: WORKFLOWS_PATH,
    end: false,
  },
  [WORKSPACE_PATH]: {
    path: WORKSPACE_PATH,
    end: false,
  },
  [SETTINGS_PATH]: {
    path: SETTINGS_PATH,
    end: false,
  },
  [APP_CONNECTIONS_PATH]: {
    path: APP_CONNECTIONS_PATH,
    end: false,
  },
};

export const WORKFLOW_ID_ROUTE_KEY = ':workflowId';
export const WORKFLOW_STEP_ID_ROUTE_KEY = ':stepId';
export const WORKFLOW_FOLDER_ID_ROUTE_KEY = ':folderId';
export const SUBMISSION_ID_ROUTE_KEY = ':id';

export const WORKSPACE_PATHS = {
  TASK_DETAILS: 'task-details',
  SUBMISSION: 'submission',
  WORKFLOW: 'workflow',
};

export const PATH_QUERY_PARAMETER_NAMES = {
  SEARCH_TERM: 'searchTerm',
  SEARCH_BY: 'searchBy',
  VALUE: 'value',
  INDUSTRY: 'industry',
  NEW_WORKFLOW: 'newWorkflow',
  TEMPLATE_ID: 'templateId',
};

export enum DragTypes {
  Connectors = 'Connectors',
  Fields = 'Fields',
  MetaFields = 'MetaFields',
  Rules = 'Rules',
}

export const DISALLOWED_AS_FIRST_STEP_CONNECTORS = [
  ConnectorIds.Documents,
  ConnectorIds.Sign,
  ConnectorIds.StepLogic,
  ConnectorIds.Approval,
];

export const ORGANIZATION_PRODUCT_ID = 'organization';
export const ADMIN_ROLE = 'admin';

export enum LogicOperator {
  any = 'any',
  all = 'all',
}

export const DEFAULT_FOLDER_ID = 'uncategorized';
export const DELETED_FOLDER_ID = 'deleted';
export const NAMED_FOLDER_IDS = [DEFAULT_FOLDER_ID, DELETED_FOLDER_ID];

export const ELEMENT_PARAGRAPH = 'p';
export const ELEMENT_IMAGE = 'img';

export const ADDON_REQUIRED_FEATURES = Object.values(AdditionalFeatures);
